import React, { useState } from 'react';
import { useAnalysisInbox } from '../hooks/useAnalysisInbox';
import MarkdownRenderer from './MarkdownRenderer';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader } from './ui/card';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { 
  Mail, 
  RefreshCw, 
  ArrowLeft, 
  Copy, 
  Trash2, 
  Clock, 
  Loader2, 
  CheckCircle, 
  XCircle, 
  HelpCircle,
  AlertTriangle
} from 'lucide-react';

const AnalysisInboxModal = ({ isOpen, onClose }) => {
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const { 
    analyses, 
    isUpdating, 
    updatePendingAnalyses, 
    markAsRead, 
    deleteAnalysis 
  } = useAnalysisInbox();

  const handleAnalysisClick = (analysis) => {
    if (analysis.status === 'completed' && !analysis.isRead) {
      markAsRead(analysis.id);
    }
    setSelectedAnalysis(analysis);
  };

  const handleDelete = (analysisId, e) => {
    if (e) e.stopPropagation();
    if (window.confirm('Удалить этот анализ?')) {
      deleteAnalysis(analysisId);
      if (selectedAnalysis && selectedAnalysis.id === analysisId) {
        setSelectedAnalysis(null);
      }
    }
  };

  const handleBackToList = () => {
    setSelectedAnalysis(null);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'processing':
        return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <HelpCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return 'В очереди';
      case 'processing':
        return 'Обрабатывается';
      case 'completed':
        return 'Готов';
      case 'failed':
        return 'Ошибка';
      default:
        return 'Неизвестно';
    }
  };

  const getStatusVariant = (status) => {
    switch (status) {
      case 'pending':
        return 'secondary';
      case 'processing':
        return 'default';
      case 'completed':
        return 'default';
      case 'failed':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[85vw] h-[92vh] max-w-[85vw] flex flex-col p-0 sm:max-w-[85vw]" showCloseButton={false}>
        <DialogHeader className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Анализы
            </DialogTitle>
            <DialogDescription className="sr-only"> </DialogDescription>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={updatePendingAnalyses}
                disabled={isUpdating}
                className="gap-2"
              >
                {isUpdating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
                Обновить
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-8 w-8 p-0"
              >
                <XCircle className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          {!selectedAnalysis ? (
            // Список анализов
            analyses.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
                <Mail className="h-16 w-16 text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold text-muted-foreground mb-2">
                  Анализов пока нет
                </h3>
                <p className="text-sm text-muted-foreground">
                  Запустите анализ любого виджета, чтобы увидеть результаты здесь
                </p>
              </div>
            ) : (
              <ScrollArea className="h-full">
                <div className="p-4 space-y-3">
                  {analyses.map((analysis) => (
                    <Card
                      key={analysis.id}
                      className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
                        !analysis.isRead && analysis.status === 'completed' 
                          ? 'border-l-4 border-l-primary' 
                          : ''
                      }`}
                      onClick={() => handleAnalysisClick(analysis)}
                    >
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            {getStatusIcon(analysis.status)}
                            <h4 className="font-semibold">
                              {analysis.widgetName}
                            </h4>
                            {!analysis.isRead && analysis.status === 'completed' && (
                              <Badge variant="default" className="text-xs">
                                NEW
                              </Badge>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => handleDelete(analysis.id, e)}
                            className="h-8 w-8 p-0"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardHeader>
                      
                      <CardContent className="pt-0">
                        <div className="flex items-center justify-between text-sm">
                          <Badge variant={getStatusVariant(analysis.status)} className="gap-1">
                            {getStatusIcon(analysis.status)}
                            {getStatusText(analysis.status)}
                          </Badge>
                          <span className="text-muted-foreground text-xs">
                            {formatDate(analysis.createdAt)}
                          </span>
                        </div>

                        {analysis.status === 'failed' && analysis.error && (
                          <div className="mt-3 p-2 bg-destructive/10 border border-destructive/20 rounded-md">
                            <div className="flex items-center gap-2 text-destructive text-sm">
                              <XCircle className="h-4 w-4" />
                              {analysis.error}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </ScrollArea>
            )
          ) : (
            // Детали анализа
            <div className="flex flex-col h-full">
              <div className="flex items-center justify-between p-4 border-b">
                <Button
                  variant="ghost"
                  onClick={handleBackToList}
                  className="gap-2"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Назад к списку
                </Button>
                
                <div className="flex items-center gap-3">
                  {getStatusIcon(selectedAnalysis.status)}
                  <h3 className="font-semibold text-lg">{selectedAnalysis.widgetName}</h3>
                </div>
                
                <div className="flex items-center gap-2">
                  {selectedAnalysis.status === 'completed' && selectedAnalysis.result && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        navigator.clipboard.writeText(selectedAnalysis.result.analysis);
                      }}
                      className="gap-2"
                    >
                      <Copy className="h-4 w-4" />
                      Копировать
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(selectedAnalysis.id)}
                    className="gap-2"
                  >
                    <Trash2 className="h-4 w-4" />
                    Удалить
                  </Button>
                </div>
              </div>

              <Card className="m-4 mb-0">
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Статус:</span>
                      <Badge variant={getStatusVariant(selectedAnalysis.status)} className="gap-1">
                        {getStatusIcon(selectedAnalysis.status)}
                        {getStatusText(selectedAnalysis.status)}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">Создан:</span>
                      <span className="text-muted-foreground">{formatDate(selectedAnalysis.createdAt)}</span>
                    </div>
                    {selectedAnalysis.completedAt && (
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Завершен:</span>
                        <span className="text-muted-foreground">{formatDate(selectedAnalysis.completedAt)}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="font-medium">ID задачи:</span>
                      <code className="text-xs bg-muted px-2 py-1 rounded">
                        {selectedAnalysis.taskId}
                      </code>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="flex-1 overflow-hidden p-4 pt-0">
                {selectedAnalysis.status === 'pending' && (
                  <Card className="h-full flex items-center justify-center">
                    <CardContent className="text-center p-8">
                      <Clock className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
                      <h4 className="text-lg font-semibold mb-2">Анализ в очереди</h4>
                      <p className="text-muted-foreground">
                        Ваш запрос добавлен в очередь обработки. Пожалуйста, подождите.
                      </p>
                    </CardContent>
                  </Card>
                )}

                {selectedAnalysis.status === 'processing' && (
                  <Card className="h-full flex items-center justify-center">
                    <CardContent className="text-center p-8">
                      <Loader2 className="h-16 w-16 text-blue-500 mx-auto mb-4 animate-spin" />
                      <h4 className="text-lg font-semibold mb-2">Анализ обрабатывается</h4>
                      <p className="text-muted-foreground">
                        Искусственный интеллект анализирует данные вашего виджета. Это может занять несколько минут.
                      </p>
                    </CardContent>
                  </Card>
                )}

                {selectedAnalysis.status === 'failed' && (
                  <Card className="h-full flex items-center justify-center border-destructive">
                    <CardContent className="text-center p-8">
                      <XCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
                      <h4 className="text-lg font-semibold mb-4 text-destructive">Ошибка при анализе</h4>
                      <Card className="bg-destructive/10 border-destructive/20">
                        <CardContent className="p-4">
                          <code className="text-sm text-destructive break-words">
                            {selectedAnalysis.error || 'Произошла неизвестная ошибка'}
                          </code>
                        </CardContent>
                      </Card>
                    </CardContent>
                  </Card>
                )}

                {selectedAnalysis.status === 'completed' && selectedAnalysis.result && (
                  <Card className="h-full flex flex-col">
                    <CardContent className="flex-1 overflow-hidden p-0">
                      <ScrollArea className="h-full">
                        <div className="p-6">
                          <MarkdownRenderer 
                            content={selectedAnalysis.result.analysis}
                            className="prose prose-slate max-w-none"
                          />
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>
                )}

                {selectedAnalysis.status === 'completed' && !selectedAnalysis.result && (
                  <Card className="h-full flex items-center justify-center border-yellow-500">
                    <CardContent className="text-center p-8">
                      <AlertTriangle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
                      <h4 className="text-lg font-semibold mb-2 text-yellow-600">Результат недоступен</h4>
                      <p className="text-muted-foreground">
                        Анализ был завершен, но результат не сохранился. Попробуйте запустить анализ заново.
                      </p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AnalysisInboxModal;
